import * as React from 'react'
import { Plus, Trash2, Code, List } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'
import { cn } from '@/lib/utils'

type ValueType = 'number' | 'boolean' | 'string'

interface KeyValueEditorProps {
  data: Record<string, unknown>
  onChange: (data: Record<string, unknown>) => void
  valueType?: ValueType
  className?: string
  keyPlaceholder?: string
  valuePlaceholder?: string
}

export function KeyValueEditor({
  data,
  onChange,
  valueType = 'string',
  className,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
}: KeyValueEditorProps) {
  const [showJson, setShowJson] = React.useState(false)
  const [newKey, setNewKey] = React.useState('')
  const [newValue, setNewValue] = React.useState<string>('')
  const [jsonText, setJsonText] = React.useState('')
  const [jsonError, setJsonError] = React.useState<string | null>(null)

  const entries = Object.entries(data)

  const handleAddRow = () => {
    if (!newKey.trim()) return

    let parsedValue: unknown = newValue
    if (valueType === 'number') {
      parsedValue = parseFloat(newValue) || 0
    } else if (valueType === 'boolean') {
      parsedValue = true
    }

    onChange({
      ...data,
      [newKey.trim()]: parsedValue,
    })
    setNewKey('')
    setNewValue('')
  }

  const handleRemoveRow = (key: string) => {
    const newData = { ...data }
    delete newData[key]
    onChange(newData)
  }

  const handleValueChange = (key: string, value: string) => {
    let parsedValue: unknown = value
    if (valueType === 'number') {
      parsedValue = parseFloat(value) || 0
    }
    onChange({
      ...data,
      [key]: parsedValue,
    })
  }

  const handleBooleanChange = (key: string, checked: boolean) => {
    onChange({
      ...data,
      [key]: checked,
    })
  }

  const handleJsonEdit = () => {
    try {
      const parsed = JSON.parse(jsonText)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setJsonError('Must be a valid JSON object')
        return
      }
      onChange(parsed)
      setJsonError(null)
    } catch {
      setJsonError('Invalid JSON syntax')
    }
  }

  const toggleJsonView = () => {
    if (!showJson) {
      setJsonText(JSON.stringify(data, null, 2))
      setJsonError(null)
    }
    setShowJson(!showJson)
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Toggle between table and JSON view */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleJsonView}
          className="gap-2"
        >
          {showJson ? (
            <>
              <List className="h-4 w-4" />
              Table View
            </>
          ) : (
            <>
              <Code className="h-4 w-4" />
              JSON View
            </>
          )}
        </Button>
      </div>

      {showJson ? (
        /* JSON Editor View */
        <div className="space-y-2">
          <textarea
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value)
              setJsonError(null)
            }}
            className="w-full h-48 font-mono text-sm p-3 rounded-md border border-input bg-background"
            placeholder='{"key": "value"}'
          />
          {jsonError && (
            <p className="text-sm text-destructive">{jsonError}</p>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleJsonEdit}
          >
            Apply JSON
          </Button>
        </div>
      ) : (
        /* Table View */
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-[1fr_1fr_40px] gap-2 text-sm font-medium text-muted-foreground">
            <div>Key</div>
            <div>Value</div>
            <div></div>
          </div>

          {/* Existing rows */}
          {entries.map(([key, value]) => (
            <div key={key} className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center">
              <Input
                value={key}
                disabled
                className="bg-muted"
              />
              {valueType === 'boolean' ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => handleBooleanChange(key, e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span className="text-sm">
                    {Boolean(value) ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              ) : (
                <Input
                  type={valueType === 'number' ? 'number' : 'text'}
                  value={String(value)}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveRow(key)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          {/* Add new row */}
          <div className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center pt-2 border-t">
            <Input
              placeholder={keyPlaceholder}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddRow()
                }
              }}
            />
            {valueType === 'boolean' ? (
              <span className="text-sm text-muted-foreground">
                (New features default to enabled)
              </span>
            ) : (
              <Input
                type={valueType === 'number' ? 'number' : 'text'}
                placeholder={valuePlaceholder}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddRow()
                  }
                }}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleAddRow}
              disabled={!newKey.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
